import { ArrowRight, Download, Linkedin, MapPin, Laptop, Target } from "lucide-react";
import { BRAND, PROFILE } from "../shared/Brand";

// Large Hero Logo Component - Pure ascending bars
export const HeroLogo = () => (
  <div className="relative">
    <div className="absolute inset-0 bg-[#D4AF37]/20 rounded-3xl blur-3xl scale-110" />
    <div className="relative bg-white rounded-2xl p-8 shadow-2xl border border-white/20">
      <svg width="160" height="140" viewBox="0 0 64 56" fill="none" className="mx-auto">
        <rect x="4" y="38" width="12" height="16" fill={BRAND.gold} opacity="0.5" rx="2"/>
        <rect x="19" y="26" width="12" height="28" fill={BRAND.gold} opacity="0.65" rx="2"/>
        <rect x="34" y="14" width="12" height="40" fill={BRAND.gold} opacity="0.8" rx="2"/>
        <rect x="49" y="2" width="12" height="52" fill={BRAND.gold} rx="2"/>
      </svg>
      <div className="w-24 h-0.5 bg-[#D4AF37] mx-auto mt-4 mb-3" />
      <div className="text-center">
        <span className="text-3xl font-bold tracking-wider text-[#1C1C1C]">LRS</span>
      </div>
    </div>
  </div>
);

const quickFacts = [
  { id: "location", icon: MapPin, label: "Location", value: "Florida & New Jersey · Eastern Time" },
  { id: "work", icon: Laptop, label: "Work Style", value: "Remote or Hybrid" },
  { id: "focus", icon: Target, label: "Role Focus", value: "Sales Development & GTM Leadership · B2B SaaS" }
];

const btnSecondary = "inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-[#D4AF37]/50 text-[#D4AF37] rounded-md font-medium hover:bg-[#D4AF37]/10 transition-colors";

// Hero Section - Dan Leitner personal brand
export const HeroSection = () => (
  <section className="relative min-h-[85vh] flex items-center pt-20 overflow-hidden" data-testid="hero-section">
    <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A] to-[#1C1C1C]" />

    <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-12 lg:py-20 grid lg:grid-cols-[1.4fr_1fr] gap-10 lg:gap-16 items-center">
      <div className="order-2 lg:order-1">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05] mb-4" data-testid="hero-name">
          Dan Leitner
        </h1>
        <p className="text-sm sm:text-base font-mono text-[#D4AF37] tracking-wide mb-6" data-testid="hero-title">
          Sales Development &amp; GTM Leader | Pipeline Growth | GTM Operations | AI-Enabled Outbound
        </p>
        <p className="text-xl lg:text-2xl text-white font-medium leading-snug mb-5">
          I build sales development organizations and GTM systems that create pipeline, develop future AEs, and scale.
        </p>
        <p className="text-base lg:text-lg text-zinc-300 leading-relaxed mb-5">
          I've built SDR teams from zero, led organizations of 40 SDRs through frontline leaders, rebuilt underperforming motions, and helped B2B SaaS companies connect Sales Development, Marketing, RevOps, and technology into a stronger revenue engine.
        </p>
        <p className="text-base text-zinc-200 font-medium mb-8">
          Player-coach. Builder. Operator. Still willing to pick up the phone.
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          <a href="#experience" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#D4AF37] text-[#0A0A0A] rounded-md font-semibold hover:bg-[#E8C547] transition-colors shadow-lg shadow-[#D4AF37]/20" data-testid="cta-hero-experience">
            View My Experience <ArrowRight className="w-5 h-5" />
          </a>
          <a href={PROFILE.resume} download className={btnSecondary} data-testid="cta-hero-resume">
            <Download className="w-4 h-4" /> Download Resume
          </a>
          <a href={PROFILE.linkedin} target="_blank" rel="noopener noreferrer" className={btnSecondary} data-testid="cta-hero-linkedin">
            <Linkedin className="w-4 h-4" /> LinkedIn
          </a>
        </div>

        <dl className="mt-8 pt-6 border-t border-white/10 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="quick-facts">
          {quickFacts.map((f) => (
            <div key={f.label} className="flex items-start gap-2.5" data-testid={`quick-fact-${f.id}`}>
              <f.icon className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
              <div>
                <dt className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{f.label}</dt>
                <dd className="text-sm text-zinc-200">{f.value}</dd>
              </div>
            </div>
          ))}
        </dl>
      </div>

      <div className="order-1 lg:order-2 flex justify-center">
        <div className="relative w-64 sm:w-80 lg:w-full max-w-md">
          <div className="absolute -inset-4 bg-[#D4AF37]/15 rounded-3xl blur-2xl" />
          <img
            src={PROFILE.photo}
            alt="Dan Leitner"
            className="relative w-full aspect-square object-cover rounded-2xl border border-[#D4AF37]/30"
            data-testid="hero-photo"
          />
        </div>
      </div>
    </div>
  </section>
);

export default HeroSection;
